import { useCallback, useEffect, useRef } from "react";
import { useUnit } from "effector-react";
import { $audioConfig } from "../state/appConfig";
import { $sceneLoadingState } from "../state/sceneLoading";
import { MUSIC_TRACKS } from "../audio/musicTracks";

const MUSIC_FADE_MS = 800;
const DEFAULT_MUSIC_VOLUME = 0.6;

const resolveTrack = (trackId: string | null | undefined) =>
  MUSIC_TRACKS.find((track) => track.id === trackId) ?? MUSIC_TRACKS[0] ?? null;

const resolveUrl = (src: string) => {
  if (typeof window === "undefined") {
    return src;
  }
  try {
    return new URL(src, window.location.href).toString();
  } catch {
    return src;
  }
};

const clampVolume = (value: number) =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1;

export function useAudioController(): void {
  const { soundOn, musicOn, musicTrackId, musicVolume } = useUnit($audioConfig);
  const { status: sceneLoadingStatus } = useUnit($sceneLoadingState);
  const sceneReady = sceneLoadingStatus === "ready";
  const musicAudioRef = useRef<HTMLAudioElement | null>(null);
  const fadeRafRef = useRef<number | null>(null);

  const clearFade = useCallback(() => {
    if (fadeRafRef.current) {
      cancelAnimationFrame(fadeRafRef.current);
      fadeRafRef.current = null;
    }
  }, []);

  const fadeTo = useCallback(
    (
      audio: HTMLAudioElement,
      from: number,
      to: number,
      durationMs: number,
      onComplete?: () => void
    ) => {
      clearFade();
      const start = performance.now();
      const delta = to - from;

      const step = (now: number) => {
        const elapsed = Math.min(now - start, durationMs);
        const progress = durationMs > 0 ? elapsed / durationMs : 1;
        const nextVolume = from + delta * progress;
        audio.volume = Math.min(1, Math.max(0, nextVolume));
        if (progress < 1) {
          fadeRafRef.current = requestAnimationFrame(step);
        } else {
          fadeRafRef.current = null;
          onComplete?.();
        }
      };

      fadeRafRef.current = requestAnimationFrame(step);
    },
    [clearFade]
  );

  useEffect(() => {
    const audio = musicAudioRef.current ?? new Audio();
    musicAudioRef.current = audio;
    audio.loop = true;
    audio.preload = "auto";

    return () => {
      clearFade();
      audio.pause();
      audio.src = "";
    };
  }, [clearFade]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio || typeof window === "undefined") {
      return;
    }

    const track = resolveTrack(musicTrackId);
    if (!track) {
      if (!audio.paused) {
        fadeTo(audio, audio.volume, 0, MUSIC_FADE_MS, () => audio.pause());
      }
      return;
    }

    const targetVolume = clampVolume(
      (track.volume ?? DEFAULT_MUSIC_VOLUME) * clampVolume(musicVolume)
    );
    const desiredSrc = resolveUrl(track.src);
    const currentSrc = audio.src ? resolveUrl(audio.src) : "";
    const hasNewTrack = currentSrc !== desiredSrc;

    const startPlayback = async () => {
      audio.src = track.src;
      audio.currentTime = 0;
      audio.volume = 0;
      try {
        await audio.play();
        fadeTo(audio, 0, targetVolume, MUSIC_FADE_MS);
      } catch {
        audio.pause();
      }
    };

    if (!sceneReady || !soundOn || !musicOn) {
      if (!audio.paused) {
        fadeTo(audio, audio.volume, 0, MUSIC_FADE_MS, () => audio.pause());
      }
      return;
    }

    if (hasNewTrack) {
      if (!audio.paused) {
        fadeTo(audio, audio.volume, 0, MUSIC_FADE_MS, () => {
          audio.pause();
          startPlayback();
        });
      } else {
        void startPlayback();
      }
      return;
    }

    if (audio.paused) {
      void startPlayback();
    } else {
      fadeTo(audio, audio.volume, targetVolume, MUSIC_FADE_MS);
    }
  }, [fadeTo, musicOn, musicTrackId, musicVolume, sceneReady, soundOn]);
}
